import React, { useEffect, useRef, useState } from "react"
import "./assets/mediaPlayer.css"
import { Text } from "./assets/Text"
import { songBackgrounds, songs } from "./songs"
import { CATEGORY_MAPS, getRandom } from "./assets/background"
import {
  initiateControls,
  setActionHandlers,
  setMetadata,
  updatePositionState,
} from "./mediaSession"
import { downloadSoundtrackFile, getAllFilenames, storeEvents } from "./store"
import { keys } from "idb-keyval"
import {
  LoopIcon,
  LoopOnceIcon,
  SongStatus,
  StatusIcon,
} from "./assets/ReadyCheck"

const loopStates: ("YES" | "NO" | "ONCE")[] = ["NO", "YES", "ONCE"]

const calculateTime = (secs: number) => {
  const minutes = Math.floor(secs / 60)
  const seconds = Math.floor(secs % 60)
  const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`
  if (
    minutes === Infinity ||
    seconds === Infinity ||
    isNaN(minutes) ||
    isNaN(seconds)
  ) {
    return "-:--"
  }
  return `${minutes}:${returnedSeconds}`
}
export type AudioEntry = {
  name: string
  filename: string
  duration: number
  live?: boolean
}

const Play: React.FC<React.SVGProps<SVGSVGElement>> = (props) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#e5c05c"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polygon points="5 3 19 12 5 21 5 3"></polygon>
    </svg>
  )
}
const Pause: React.FC<React.SVGProps<SVGSVGElement>> = (props) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#e5c05c"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polygon points="3 5 9 5 9 19 3 19"></polygon>
      <polygon points="14 5 20 5 20 19 14 19"></polygon>
    </svg>
  )
}

type SongProps = {
  toggleAudio: (audioSource: string) => void
  audio: AudioEntry
  current: boolean
  playing: boolean
  state: SongStatus
}

const SongContainer: React.FC<{
  children: React.ReactNode
  current: boolean
}> = ({ children, current }) => {
  if (current) {
    return (
      <li className="flex items-center gap-3 bg-black/30 hover:bg-black/50 p-2 pr-4 my-0 h-13">
        {children}
      </li>
    )
  }
  return (
    <li className="flex items-center gap-3 hover:bg-black/40 p-2 pr-4 my-0 h-13">
      {children}
    </li>
  )
}

const SongEntry: React.FC<SongProps> = ({
  audio,
  current,
  playing,
  state,
  toggleAudio,
}) => {
  const download = async () => {
    const a = document.createElement("a")
    const cache = await downloadSoundtrackFile(audio.filename)
    if (!cache) return
    const blob = new Blob([cache], { type: "audio/mp3" })
    a.href = URL.createObjectURL(blob)

    a.download = audio.filename.replace("/ost/", "")
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <SongContainer current={current}>
      <button
        aria-label="Play"
        className="p-2 hover:bg-green-500 group cursor-pointer focus:outline-none button bg-leather-blue"
        onClick={() => toggleAudio(audio.filename)}
      >
        {playing ? (
          <Pause className="w-4 h-4 group-hover:text-white" />
        ) : (
          <Play className="w-4 h-4 group-hover:text-white" />
        )}
      </button>
      <div className="flex-1 flex items-center shrink overflow-hidden h-full">
        {!audio.live ? null : (
          <div className="h-2 w-2 rounded-xl bg-red-700 mr-4"></div>
        )}{" "}
        <Text value={audio.name} />
      </div>
      <StatusIcon status={state} />
      <div className="text-xs text-gray-300">
        {audio.live ? null : <Text value={calculateTime(audio.duration)} />}
      </div>
      {/*audio.live ? null : (
        <button
          aria-label="Download"
          className="focus:outline-none pr-4 group cursor-pointer"
          onClick={download}
        >
          <svg
            className="w-4 h-4 group-hover:text-green-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5" />
          </svg>
        </button>
      )*/}
    </SongContainer>
  )
}

type AudioData = {
  max: string
  paused: boolean

  audio: AudioEntry | null
}

const buttonAudio = new Audio()
buttonAudio.src = "/homm3/ost/BUTTON.mp3"
buttonAudio.load()
buttonAudio.volume = 0.25

const playButtonAudio = () => {
  if (buttonAudio.readyState === buttonAudio.HAVE_NOTHING) {
    return
  }
  buttonAudio.currentTime = 0
  buttonAudio.play().catch((e) => {})
}

const SkipButton: React.FC<{
  onClick?: () => void
  children: React.ReactNode
  "aria-label"?: string
}> = ({ children, onClick, ...rest }) => {
  if (onClick) {
    return (
      <button
        aria-label={rest["aria-label"]}
        className="focus:outline-none text-white cursor-pointer"
        onClick={onClick}
      >
        {children}
      </button>
    )
  }
  return (
    <button
      aria-label={rest["aria-label"]}
      className="focus:outline-none cursor-default opacity-50"
    >
      {children}
    </button>
  )
}

const LOOP_KEY = "loopState"

export const MediaPlayer: React.FC<{
  audios: AudioEntry[]
}> = ({ audios }) => {
  const [filesState, setFilesState] = useState(
    {} as Record<string, "LOADING" | "READY" | "ERROR">
  )

  let globalState = "UNKNOWN" as SongStatus

  const allValues = Object.values(filesState)

  if (allValues.includes("LOADING")) {
    globalState = "LOADING"
  } else if (
    allValues.length === audios.length &&
    allValues.every((s) => s === "READY")
  ) {
    globalState = "READY"
  }

  const startAllDownloading = () => {
    if (globalState === "LOADING") return
    navigator.storage?.persist?.().catch(() => {})
    audios.forEach((a) => downloadSoundtrackFile(a.filename))
  }

  const audioFile = useRef<HTMLAudioElement>(null)
  const [currentTime, setCurrentTime] = useState("0:00")
  const [loop, setLoop] = useState(
    window.localStorage.getItem(LOOP_KEY) as (typeof loopStates)[number]
  )

  const [audioData, setAudioData] = useState<AudioData>({
    max: "0:00",
    paused: true,
    audio: null,
  })
  const audioDataEntry = useRef<AudioData["audio"]>(audioData.audio)
  audioDataEntry.current = audioData.audio

  const setFileState = (
    filename: string,
    state: (typeof filesState)[keyof typeof filesState]
  ) => {
    setFilesState((p) => ({ ...p, [filename]: state }))
  }

  useEffect(() => {
    if (
      audioFile.current &&
      !audioFile.current.getAttribute("data-filename") &&
      audios[0]
    ) {
      audioFile.current.src = audios[0].filename

      const playNext = () => {
        const next = audios.find(
          (_, i) => audios[i - 1] === audioDataEntry.current
        )
        if (!next) return
        toggleAudio(next.filename)
      }
      const playPrevious = () => {
        const previous = audios.find(
          (_, i) => audios[i + 1] === audioDataEntry.current
        )
        if (!previous) return
        toggleAudio(previous.filename)
      }

      initiateControls(audioFile.current, playNext, playPrevious)
    }
    const reloadKeys = async () => {
      getAllFilenames()
        .then((keys) => {
          const newKeys = (keys as string[]).filter(key => audios.some(audio => audio.filename === key)).reduce(
            (a, b) => ({ ...a, [b]: "READY" as const }),
            {} as typeof filesState
          )

          setFilesState((p) => ({ ...p, ...newKeys }))
        })
        .catch(() => {})
    }
    reloadKeys()
    storeEvents.on("downloadFinish", reloadKeys)

    const onDownloadStart = (filename: string) => {
      setFileState(filename, "LOADING")
    }

    const onDownloadFail = (filename: string) => {
      setFileState(filename, "ERROR")
    }

    storeEvents.on("downloadFailed", onDownloadFail)
    storeEvents.on("downloadStart", onDownloadStart)

    return () => {
      storeEvents.off("downloadFinish", reloadKeys)
      storeEvents.off("downloadFailed", onDownloadFail)
      storeEvents.off("downloadStart", onDownloadStart)
    }
  }, [])

  const currentAudio = audioData.audio ?? audios[0]!

  useEffect(() => {
    const seekSlider = document.getElementById(
      "seek-slider"
    ) as HTMLInputElement

    const volumeSlider = document.getElementById(
      "audio-slider"
    ) as HTMLInputElement
    const initialVolume = parseInt(
      window.localStorage.getItem("volume") || "50"
    )
    volumeSlider.style.setProperty("--seek-before-width", `${initialVolume}%`)
    volumeSlider.value = `${initialVolume}`

    if (audioFile.current) {
      audioFile.current.volume = initialVolume / 100
    }

    if (!seekSlider) return

    const whilePlaying = () => {
      const audio = audioFile.current

      if (audio) {
        seekSlider.value =
          audio.duration === Infinity ? "100" : `${audio.currentTime}`
        setCurrentTime(calculateTime(audio.currentTime))
        seekSlider.style.setProperty(
          "--seek-before-width",
          `${
            ((audio.duration === Infinity ? 100 : audio.currentTime || 0) /
              Number(seekSlider.max || 1)) *
            100
          }%`
        )
      }

      return requestAnimationFrame(whilePlaying)
    }
    const rAF = whilePlaying()

    return () => {
      window.cancelAnimationFrame(rAF)
    }
  }, [])

  const toggleAudio = async (audioSource: string, silent = false) => {
    if (!audioFile.current) {
      return
    }
    if (!silent) playButtonAudio()
    // if (
    //   currentAudio.live &&
    //   audioFile.current.duration === Infinity &&
    //   audioFile.current.paused &&
    //   audioSource === audioFile.current.currentSrc
    // ) {
    //   const src = audioFile.current.currentSrc;

    //   audioFile.current.setAttribute("src", "");

    //   setTimeout(() => {
    //     audioFile.current?.setAttribute("src", src);
    //     audioFile.current?.play();
    //   });
    //   return;
    // }
    const currentFilename =
      audioFile.current.getAttribute("data-filename") ?? ""
    if (currentFilename && audioSource === currentFilename) {
      audioFile.current.paused
        ? audioFile.current.play()
        : audioFile.current?.pause()
      return
    }
    const cache = await downloadSoundtrackFile(audioSource)
    if (cache) {
      audioFile.current.setAttribute("data-filename", audioSource)
      URL.revokeObjectURL(audioFile.current.src)
      const blob = new Blob([cache], { type: "audio/mp3" })
      audioFile.current.src = URL.createObjectURL(blob)

      audioFile.current.play()
    }
  }

  const skipAudio: React.FormEventHandler<HTMLInputElement> = (val) => {
    if (!audioFile.current || audioFile.current.duration === Infinity) {
      return
    }
    audioFile.current.currentTime = Number(
      (val.target as HTMLInputElement).value
    )
    updatePositionState()
  }

  const changeVolume: React.FormEventHandler<HTMLInputElement> = (val) => {
    if (!audioFile.current) {
      return
    }
    const volume = Number((val.target as HTMLInputElement).value)
    const volumeSlider = document.getElementById(
      "audio-slider"
    ) as HTMLInputElement

    volumeSlider.style.setProperty("--seek-before-width", `${volume}%`)
    window.localStorage.setItem("volume", `${volume}`)
    audioFile.current.volume = volume / 100
  }

  const onPauseOrPlay = () => {
    if (audioFile.current?.readyState === 0) return
    setAudioData((prev) => ({ ...prev, paused: !!audioFile.current?.paused }))
  }

  const getNextAndPrevHandlers = (
    currentFilename?: string,
    isLooped = loop
  ) => {
    const previous =
      audios.find((_, i) => audios[i + 1]?.filename === currentFilename) ??
      (isLooped !== "NO" ? audios[audios.length - 1] : null)
    const next =
      audios.find((_, i) => audios[i - 1]?.filename === currentFilename) ??
      (isLooped !== "NO" ? audios[0] : null)

    const playNext = (silent = false) => {
      if (!next) return
      toggleAudio(next.filename, silent)
    }
    const playPrevious = (silent = false) => {
      if (!previous) return
      toggleAudio(previous.filename, silent)
    }

    return {
      playNext,
      playPrevious,
      state: { next: !!next, prev: !!previous },
    }
  }

  const onLoad = () => {
    const seekSlider = document.getElementById(
      "seek-slider"
    ) as HTMLInputElement

    const audio = audioFile.current

    if (!seekSlider || !audio) return
    seekSlider.max = audio.duration === Infinity ? "100" : `${audio.duration}`
    audio.currentTime = 0

    const newAudio = audios.find((podcast) =>
      audio.duration === Infinity
        ? podcast.filename === audio.currentSrc
        : podcast.filename === audio.getAttribute("data-filename")
    )

    if (!newAudio) return

    setAudioData((prev) => ({
      ...prev,
      audio: newAudio,
      max: calculateTime(audio.duration),
      paused: !!audio.paused,
    }))
    updatePositionState()
    setMetadata(newAudio)
    const {
      playNext,
      playPrevious,
      state: { next, prev },
    } = getNextAndPrevHandlers(newAudio.filename)

    setActionHandlers(
      next ? () => playNext() : null,
      prev ? () => playPrevious() : null
    )
  }
  const {
    playNext,
    playPrevious,
    state: { next, prev },
  } = getNextAndPrevHandlers(audioData.audio?.filename)
  const type = songBackgrounds[songs[audios.indexOf(currentAudio)]!]

  const background = type
    ? CATEGORY_MAPS[type]
    : getRandom(audios.indexOf(currentAudio))

  const toggleLoop = () => {
    setLoop((p) => {
      const newLoop = loopStates[loopStates.indexOf(p) + 1] ?? loopStates[0]!
      window.localStorage.setItem(LOOP_KEY, newLoop)

      const {
        playNext,
        playPrevious,
        state: { next, prev },
      } = getNextAndPrevHandlers(currentAudio.filename, newLoop)

      setActionHandlers(
      next ? () => playNext() : null,
      prev ? () => playPrevious() : null
    )

      return newLoop
    })
  }

  const FinalLoopIcon = loop === "ONCE" ? LoopOnceIcon : LoopIcon

  return (
    <div className="dialog-box bg-leather-blue flex-1 w-full md:w-[600px] m-auto p-4 overflow-hidden flex flex-col h-full md:max-h-9/12">
      <audio
        controls
        id="current-podcast"
        ref={audioFile}
        style={{ display: "none" }}
        crossOrigin="anonymous"
        onPlay={onPauseOrPlay}
        loop={loop === "ONCE"}
        onPause={onPauseOrPlay}
        onLoadedMetadata={onLoad}
        onEnded={() => playNext(true)}
        onPlaying={updatePositionState}
      >
        <source
          src={audios[0]?.live ? audios[0].filename : undefined}
          type="audio/mp3"
        />
      </audio>
      <div className="relative">
        <div
          className="m-0 w-full h-64 bg-center bg-cover transition-[background-image]"
          style={{
            backgroundImage: `url(${background})`,
            imageRendering: "pixelated",
          }}
        />

        <div className="absolute p-4 inset-0 flex flex-col gap-2 justify-end bg-gradient-to-b from-transparent to-[#172e6f]/80 backdrop backdrop-blur-5 text-white">
          <Text
            value={
              currentAudio.name.length > 24
                ? currentAudio.name.split(" - ")[1]!
                : currentAudio.name
            }
            scale={2}
          />
          <Text
            value={`HoMM3 OST ${
              currentAudio.name.length > 24
                ? `(${currentAudio.name.split(" - ")[0]!})`
                : ""
            }`}
            scale={1}
          />
        </div>
        <a className="button bg-leather-blue player-link" href="/homm4/">
          HoMM4 Player
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m14 5 7 7-7 7M3 12h18" />
          </svg>
        </a>
      </div>
      <ul className="text-xs sm:text-base divide-y divide-[#a58a42] border-t border-[#a58a42] cursor-default pl-0 my-0 flex-1 overflow-auto">
        {audios.map((audio) => {
          return (
            <SongEntry
              toggleAudio={toggleAudio}
              key={audio.filename}
              audio={audio}
              state={
                //  availableKeys.includes(audio.filename) ? "READY" : "UNKNOWN"
                filesState[audio.filename] || "UNKNOWN"
              }
              current={currentAudio.filename === audio.filename}
              playing={
                !audioData.paused && currentAudio.filename === audio.filename
              }
            />
          )
        })}
      </ul>
      <div className=" border-b border-t border-[#a58a42]">
        <input
          id="seek-slider"
          aria-label="Duration"
          step={1 / 1000}
          type="range"
          className="w-full m-0"
          onInput={skipAudio}
        />
      </div>
      <div className="flex justify-between bg-leather text-xs font-semibold text-gray-300 px-4 py-2">
        <div
          id="current-time"
          className="w-24 flex flex-col justify-center gap-3"
        >
          <Text value={currentTime} />
          <div className="h-[9px]" />
        </div>
        <div className="flex gap-3 p-2 items-center">
          <FinalLoopIcon
            className={`w-6 h-6 cursor-pointer stroke-[7] text-[#e5c05c] ${
              loop !== "NO" ? "" : "opacity-50"
            }`}
            stroke="#e5c05c"
            onClick={toggleLoop}
          />
          <SkipButton
            aria-label="Previous"
            onClick={!prev ? undefined : () => playPrevious()}
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e5c05c"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="19 20 9 12 19 4 19 20"></polygon>
              <line x1="5" y1="19" x2="5" y2="5"></line>
            </svg>
          </SkipButton>
          <button
            aria-label="Play / Pause"
            className="rounded-full w-8 h-8 flex items-center justify-center pl-0.5 ring-2 ring-[#e5c05c] focus:outline-none cursor-pointer"
            onClick={() => toggleAudio(currentAudio.filename)}
          >
            {audioData.paused ? (
              <Play className="w-5 h-5" />
            ) : (
              <Pause className="w-5 h-5" />
            )}
          </button>
          <SkipButton aria-label="Next" onClick={!next ? undefined : () => playNext()}>
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e5c05c"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="5 4 15 12 5 20 5 4"></polygon>
              <line x1="19" y1="5" x2="19" y2="19"></line>
            </svg>
          </SkipButton>
          <div className="w-6">
            <StatusIcon status={globalState} onClick={startAllDownloading} />
          </div>
        </div>
        {audioFile.current?.duration === Infinity ? (
          <div className="w-12 text-right text-red-700 font-bold">LIVE</div>
        ) : (
          <div className="w-24 text-right flex flex-col items-end gap-3 justify-center ">
            <Text value={audioData.max} />
            <div className="w-20">
              <input
                aria-label="Volume"
                id="audio-slider"
                defaultValue={50}
                step={1}
                min={0}
                max={100}
                type="range"
                className="w-full m-0 small"
                onInput={changeVolume}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
